import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  Injector,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import { TextAlignment } from '@readium/navigator';
import { debounceTime } from 'rxjs';
import { OdlSelectDirective } from '../../../directives/forms/odl-select-form-accessor';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags';
import { CustomFontsService } from '../../../services/readium/settings/custom-fonts';
import { SettingsService } from '../../../services/readium/settings/settings';
import { ViewportService } from '../../../services/viewport/viewport';
import { RangeSliderComponent } from '../../range-slider/range-slider';

@Component({
  selector: 'nr-text-settings',
  imports: [TranslatePipe, ReactiveFormsModule, OdlSelectDirective, RangeSliderComponent],
  templateUrl: './text-settings.html',
  styleUrl: './text-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TextSettingsComponent {
  private static readonly DEBOUNCE_TIME = 300;
  private static readonly DEFAULT_FONT_SIZE = 1;
  private static readonly DEFAULT_LINE_HEIGHT = 1.5;
  private static readonly DEFAULT_MARGIN_PERCENTAGE = 0.5;

  private readonly settingsService = inject(SettingsService);
  private readonly viewportService = inject(ViewportService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly customFontsService = inject(CustomFontsService);
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);
  /** `undefined` until ngx-translate has actually finished loading a language once. */
  private readonly langLoaded = toSignal(this.translateService.onLangChange);
  readonly fullSettingsEnabled = this.featureFlagsService.isEnabled('fullSettings');
  readonly settings = this.settingsService.settings;
  readonly fontSize = computed(
    () => this.settings()?.fontSize ?? TextSettingsComponent.DEFAULT_FONT_SIZE,
  );
  readonly lineHeight = computed(
    () => this.settings()?.lineHeight ?? TextSettingsComponent.DEFAULT_LINE_HEIGHT,
  );
  readonly marginPercentage = computed(() => {
    const currentMargin = this.settings()?.pageGutter;
    if (currentMargin == null) {
      return TextSettingsComponent.DEFAULT_MARGIN_PERCENTAGE;
    }
    return this.viewportService.calculatePercentageFromMargin(currentMargin);
  });
  readonly fontFamilyMap = this.customFontsService.fontsToShow.map((font) => ({
    value: font,
    label: font.replaceAll(/([a-z])([A-Z])/g, '$1 $2'),
  }));
  readonly fontFamily = computed(() => this.settings()?.fontFamily ?? 'default');
  readonly selectFontFamily = new FormControl('');
  readonly sliderFontSize = new FormControl(this.fontSize());
  readonly sliderLineHeight = new FormControl(this.lineHeight());
  readonly sliderMargin = new FormControl(this.marginPercentage());
  readonly align = computed(() => this.settings()?.textAlign);

  constructor() {
    // Change with signal forms when they are available: https://angular.io/guide/reactive-forms#reactive-forms-with-signals
    effect(() => {
      this.sliderFontSize.setValue(this.fontSize(), { emitEvent: false });
      this.sliderLineHeight.setValue(this.lineHeight(), { emitEvent: false });
      this.sliderMargin.setValue(this.marginPercentage(), { emitEvent: false });
    });

    effect(() => {
      const fontFamily = this.fontFamily();
      if (!this.langLoaded()) return;

      afterNextRender(
        () => {
          this.selectFontFamily.setValue(fontFamily, { emitEvent: false });
        },
        { injector: this.injector },
      );
    });

    this.selectFontFamily.valueChanges.subscribe((val) => {
      this.settingsService.updateReaderSettings({
        fontFamily: val,
      });
    });

    this.sliderFontSize.valueChanges
      .pipe(debounceTime(TextSettingsComponent.DEBOUNCE_TIME))
      .subscribe((val) => {
        this.settingsService.updateReaderSettings({
          fontSize: Number(val),
        });
      });
    this.sliderLineHeight.valueChanges
      .pipe(debounceTime(TextSettingsComponent.DEBOUNCE_TIME))
      .subscribe((val) => {
        this.settingsService.updateReaderSettings({
          lineHeight: Number(val),
        });
      });
    this.sliderMargin.valueChanges
      .pipe(debounceTime(TextSettingsComponent.DEBOUNCE_TIME))
      .subscribe((val) => {
        this.settingsService.updateReaderSettings({
          pageGutter: this.viewportService.calculateMarginFromPercentage(Number(val)),
        });
      });
  }

  changeTextAlign(event: OdlInputEvent): void {
    const align = event.detail.value;

    if (!TextSettingsComponent.isValidAlignment(align)) {
      return;
    }

    this.settingsService.updateReaderSettings({
      textAlign: TextAlignment[align],
    });
  }

  private static isValidAlignment(align: string): align is 'start' | 'left' | 'justify' {
    return ['start', 'left', 'justify'].includes(align);
  }
}
